import { useEffect, useRef, useState } from "react";

import {
  getSocketManager,
  initializeSocketManager,
} from "@/redux/slices/configSlice";
import { useAppDispatch, useAppSelector } from "@/redux/store";

export type ConnectionHealthStatus = "healthy" | "degraded" | "offline";

export interface ConnectionHealth {
  isTabVisible: boolean;
  isOnline: boolean;
  isSocketConnected: boolean;
  isSocketReconnectFailed: boolean;
  reconnectAttempts: number;
  status: ConnectionHealthStatus;
}

interface DeriveStatusInput {
  isOnline: boolean;
  isSocketConnected: boolean;
  isSocketReconnectFailed: boolean;
  isTabVisible: boolean;
  reconnectAttempts: number;
}

const deriveStatus = (input: DeriveStatusInput): ConnectionHealthStatus => {
  const {
    isOnline,
    isSocketConnected,
    isSocketReconnectFailed,
    isTabVisible,
    reconnectAttempts,
  } = input;

  if (!isOnline || isSocketReconnectFailed) {
    return "offline";
  }

  if (!isTabVisible || !isSocketConnected || reconnectAttempts > 0) {
    return "degraded";
  }

  return "healthy";
};

/**
 * Monitors connection-related signals and exposes a derived connection state.
 *
 * @returns {ConnectionHealth} Combined browser and socket connection state.
 */
export const useConnectionHealth = (): ConnectionHealth => {
  const socketManagerRevision = useAppSelector(
    (state) => state.config.socketManagerRevision,
  );

  const [isTabVisible, setIsTabVisible] = useState<boolean>(
    typeof document !== "undefined"
      ? document.visibilityState === "visible"
      : true,
  );

  useEffect(() => {
    const handleVisibilityChange = (): void => {
      setIsTabVisible(document.visibilityState === "visible");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return (): void => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );

  useEffect(() => {
    const handleOnline = (): void => setIsOnline(true);
    const handleOffline = (): void => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return (): void => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const dispatch = useAppDispatch();
  const autoRecoveryFiredRef = useRef(false);

  const [isSocketConnected, setIsSocketConnected] = useState<boolean>(false);
  const [isSocketReconnectFailed, setIsSocketReconnectFailed] =
    useState<boolean>(false);
  const [reconnectAttempts, setReconnectAttempts] = useState<number>(0);

  useEffect(() => {
    const manager = getSocketManager();

    if (!manager) {
      setIsSocketConnected(false);
      setIsSocketReconnectFailed(false);
      setReconnectAttempts(0);

      return undefined;
    }

    setIsSocketConnected(manager.engine?.readyState === "open");

    const handleOpen = (): void => {
      setIsSocketConnected(true);
      setIsSocketReconnectFailed(false);
      setReconnectAttempts(0);
    };

    const handleClose = (): void => {
      setIsSocketConnected(false);
    };

    const handleReconnectAttempt = (attempt: number): void => {
      setIsSocketConnected(false);
      setReconnectAttempts(attempt);
    };

    const handleReconnect = (): void => {
      setIsSocketConnected(true);
      setIsSocketReconnectFailed(false);
      setReconnectAttempts(0);
    };

    const handleReconnectFailed = (): void => {
      setIsSocketConnected(false);
      setIsSocketReconnectFailed(true);
    };

    manager.on("open", handleOpen);
    manager.on("close", handleClose);
    manager.on("reconnect_attempt", handleReconnectAttempt);
    manager.on("reconnect", handleReconnect);
    manager.on("reconnect_failed", handleReconnectFailed);

    return (): void => {
      manager.off("open", handleOpen);
      manager.off("close", handleClose);
      manager.off("reconnect_attempt", handleReconnectAttempt);
      manager.off("reconnect", handleReconnect);
      manager.off("reconnect_failed", handleReconnectFailed);
    };
  }, [socketManagerRevision]);

  useEffect(() => {
    if (isSocketConnected) {
      autoRecoveryFiredRef.current = false;
      return;
    }

    if (!isOnline || !isTabVisible) {
      autoRecoveryFiredRef.current = false;
      return;
    }

    if (!isSocketReconnectFailed) {
      return;
    }

    if (autoRecoveryFiredRef.current) {
      return;
    }

    autoRecoveryFiredRef.current = true;
    setIsSocketReconnectFailed(false);
    setReconnectAttempts(0);
    dispatch(initializeSocketManager({ isAutoRecovery: true }));
  }, [
    isSocketConnected,
    isSocketReconnectFailed,
    isTabVisible,
    isOnline,
    dispatch,
  ]);

  const status = deriveStatus({
    isOnline,
    isSocketConnected,
    isSocketReconnectFailed,
    isTabVisible,
    reconnectAttempts,
  });

  return {
    isTabVisible,
    isOnline,
    isSocketConnected,
    isSocketReconnectFailed,
    reconnectAttempts,
    status,
  };
};
